import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Loading } from "@alm/flow-ds/components";
import Element from "../Element";
import useForm from "hooks/useForm";
import useLinkedForm from "hooks/useLinkedForm";
import { WorkbenchLayer } from "components/Workbench";
import { WorkbenchContext } from "contexts/WorkbenchContext";
import TaskPageTemplate from "components/Templates/TaskPageTemplate";
import PageHeader from "components/PageHeader";
import LinkedForm from "./LinkedForm";
import { useTranslation } from "react-i18next";
import ReactFocusLock from "react-focus-lock";
import ErrorBoundary from "components/Errors/ErrorBoundary";
import UnsavedChanges from "../UnsavedChanges";
import Breadcrumb from "components/Breadcrumb";
import { State } from "data";
import { useSelector } from "react-redux";
import { useMaxFormWidth } from "../TaskForm";

type Props = {
	fid: string;
	workbenchIndex: number;
	closeForm: () => void;
	formAction: "CREATE" | "EDIT" | "VIEW";
	setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
	breadcrumb: { name: string; link?: string }[];
};

function ViewForm({
	fid,
	workbenchIndex,
	closeForm,
	formAction,
	setIsDirty,
	breadcrumb,
}: Props) {
	const { t } = useTranslation();

	const formLayout = useSelector(
		(state: State) => state?.forms[fid]?.server?.formLayout
	);

	// Get the current form
	const form = useForm(fid);

	// Get linked form
	const linked = useLinkedForm(fid, workbenchIndex);

	const [closePrompt, setClosePrompt] = useState(false);

	// Scroll active or not
	const [isScrollActive, setIsScrollActive] = useState(false);

	// Scrolling element
	const formWindow = document.querySelector(`.workbench-layer:last-child`);

	// Build the breadcrumb
	const crumb = useMemo(
		() => [
			...breadcrumb,
			{
				name: form.label,
			},
		],
		[breadcrumb, form.label]
	);

	/**
	 * Load the child form if there is a linked form
	 */
	useEffect(() => {
		if (form.ready && !linked.init && linked.hasChild) {
			linked.loadChild();
		}
	}, [linked, linked.hasChild, form.ready]);

	// Index of the current form
	const wbContext = useMemo(() => {
		return {
			workbenchIndex,
		};
	}, [workbenchIndex]);

	// Close form instance
	const close = useCallback(() => {
		form.close();
	}, [form]);

	// Safe close form instance if dirty
	const safeClose = useCallback(() => {
		if (form.dirty && !form.error) {
			setClosePrompt(true);
		} else {
			close();
		}
	}, [close, form.dirty, form.error]);

	useEffect(() => {
		// If scroll is bigger than the breadcrumb we set scroll is active
		formWindow?.addEventListener("scroll", () => {
			if (formWindow.scrollTop > 60) {
				setIsScrollActive(true);
			} else {
				setIsScrollActive(false);
			}
		});
	}, [formWindow]);

	// If Form instance is closed then run passed closeForm
	useEffect(() => {
		if (form.status === "CLOSED") closeForm();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.status]);

	const { maxWidth, maxFormWidth } = useMaxFormWidth(formLayout);

	if (form.error) {
		console.error(form.error);
		return null;
	}

	return (
		<>
			<WorkbenchLayer>
				<ErrorBoundary>
					<ReactFocusLock
						className="workbench-layer__editor"
						returnFocus={true}>
						<Breadcrumb
							crumb={crumb}
							className="container"
							style={{ maxWidth }}
						/>
						<PageHeader
							title={form.label || ""}
							sticky
							style={{ maxWidth }}
							className="container page-header__title"
							scroll={isScrollActive}>
							<div className="page-header__actions">
								{form.patching && (
									<Loading className="elem__loader--md me-3" />
								)}
								<div className="page-header__actions-buttons">
									<Button
										className="btn-outline-primary"
										disabled={!form.canClose}
										onClick={
											formAction !== "VIEW"
												? safeClose
												: close
										}
										label={t("common.labels.close")}
									/>
									{formAction !== "VIEW" && (
										<Button
											className="btn-primary"
											disabled={!form.canSave}
											onClick={() => {
												form.save();
												setIsDirty(true);
											}}
											label={t("common.labels.save")}
										/>
									)}
								</div>
							</div>
						</PageHeader>
						<div
							className="container"
							style={{
								maxWidth,
							}}>
							<div className="page-content">
								<WorkbenchContext.Provider value={wbContext}>
									<TaskPageTemplate
										style={{
											maxWidth: maxFormWidth,
										}}>
										<header>
											<h2 className="form__label">
												{form.label}
											</h2>
										</header>

										{!form.ready ? (
											<Loading className="elem__loader--lg mt-5" />
										) : (
											<Element
												rowIndex={0}
												fid={fid}
												uuid={form.uuid}
											/>
										)}
									</TaskPageTemplate>
								</WorkbenchContext.Provider>
							</div>
						</div>

						{closePrompt && form.ready && (
							<UnsavedChanges
								closeBtnClick={form.close}
								saveAndCloseBtnClick={() => {
									form.saveAndClose();
									setIsDirty(true);
								}}
								cancelBtnClick={() => setClosePrompt(false)}
							/>
						)}
					</ReactFocusLock>

					{linked.init && (
						<WorkbenchLayer>
							{linked.child ? (
								<LinkedForm
									fid={linked.child}
									workbenchIndex={workbenchIndex + 1}
									breadcrumb={crumb}
								/>
							) : (
								<div className="workbench-layer__editor">
									<Loading className="elem__loader--lg mt-5" />
								</div>
							)}
						</WorkbenchLayer>
					)}
				</ErrorBoundary>
			</WorkbenchLayer>
		</>
	);
}

export default React.memo(ViewForm);
